import React from "react";
import { CameraOff, KeyboardOff, MicOff, VideoOff, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import ModalDragStrip from "../ModalDragStrip";

type CapturePrivacyModalProps = {
  open: boolean;
  onClose: () => void;
};

const captureDetails = [
  {
    icon: KeyboardOff,
    titleKey: "no_typed_content",
    detailKey: "no_typed_content_detail",
  },
  {
    icon: VideoOff,
    titleKey: "no_continuous_recording",
    detailKey: "no_continuous_recording_detail",
  },
  {
    icon: MicOff,
    titleKey: "no_audio_or_microphone",
    detailKey: null,
  },
  {
    icon: CameraOff,
    titleKey: "no_camera_access",
    detailKey: null,
  },
] as const;

const CapturePrivacyModal = ({ open, onClose }: CapturePrivacyModalProps) => {
  const { t } = useTranslation();

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 px-4 py-8"
      aria-labelledby="capture-privacy-title"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t("close")}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <ModalDragStrip />
      <div className="relative z-10 w-full max-w-lg rounded-xl border border-outline bg-surface p-5 text-text shadow-2xl">
        <button
          type="button"
          aria-label={t("close")}
          onClick={onClose}
          className="absolute right-3 top-3 rounded-lg p-1.5 text-text-dim transition hover:bg-muted-surface hover:text-text"
        >
          <X size={18} />
        </button>

        <div className="pr-8">
          <h2 id="capture-privacy-title" className="text-lg font-semibold text-text">
            {t("what_platonic_records")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-text-mid">{t("capture_privacy_summary")}</p>
        </div>

        <div className="mt-5 space-y-3">
          {captureDetails.map(({ icon: Icon, titleKey, detailKey }) => (
            <div key={titleKey} className="flex items-center gap-3 rounded-lg bg-muted-surface p-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface text-info">
                <Icon size={18} />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm text-text">
                  <span className="font-semibold">{t(titleKey)}</span>
                  {detailKey && (
                    <span className="mt-1 block text-sm font-normal leading-5 text-text-mid">
                      {t(detailKey)}
                    </span>
                  )}
                </h3>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-outline bg-surface px-3 py-2 text-sm font-medium text-text transition hover:bg-muted-surface"
          >
            {t("close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CapturePrivacyModal;
